import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminDashboardComponents from '../../components/HigherOrderComponents/AdminDashboardComponents';
import {
    getAllMaterialData,
    getCncMachineDataById,
    upsertMachine,
    upsetMrrValues,
} from '../../utils/actions/adminActions';
import { Form } from 'react-bootstrap';
import BackButton from '../../components/BackButton/BackButton';
import { ArrowUpIcon, LucidLoader } from '../../components/Icons';
import useDocumentTitle from '../../utils/useDocumentTitle';
import GenericLoaderComponent from '../Loading/GenericLoaderComponent';

const MachineMaterialSelect = () => {
    useDocumentTitle('Machine material');

    const [allMaterials, setAllMaterials] = useState([]);
    const [data, setData] = useState({});
    const [sortType, setSortType] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    if (!location.state || !location?.state?._id) {
        navigate('/admin/machine-cnc');
        return null;
    }

    const { _id } = location.state;

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await getAllMaterialData(_id);
                const machineResponse = await getCncMachineDataById(_id);

                if (!response?.success || !machineResponse?.success) throw new Error();

                const sorted = response.data.sort((a, b) => {
                    const cmp1 = a.cnc_material.localeCompare(b.cnc_material);
                    if (cmp1 !== 0) return cmp1;
                    return a.cnc_grade.localeCompare(b.cnc_grade);
                });

                // Attach MRR state to each material row
                const enriched = sorted.map((mat) => ({
                    ...mat,
                    mrr: {
                        cnc_roughing_MRR_mm3_per_min: mat?.mmrData?.cnc_roughing_MRR_mm3_per_min || 0,
                        cnc_finishing_MRR_mm3_per_min: mat?.mmrData?.cnc_finishing_MRR_mm3_per_min || 0,
                    },
                }));

                setAllMaterials(enriched);
                setData(machineResponse.data);
            } catch (err) {
                toast.error('Data fetch failed. Please try again later');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [_id]);

    const toggleSortType = () => {
        setSortType((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    };

    useEffect(() => {
        if (allMaterials.length > 0 && sortType) {
            const sorted = [...allMaterials].sort((a, b) => {
                const cmp = sortType === 'desc'
                    ? a.cnc_material.localeCompare(b.cnc_material)
                    : b.cnc_material.localeCompare(a.cnc_material);
                return cmp || (
                    sortType === 'desc'
                        ? a.cnc_grade.localeCompare(b.cnc_grade)
                        : b.cnc_grade.localeCompare(a.cnc_grade)
                );
            });
            setAllMaterials(sorted);
        }
    }, [sortType]);

    const handleCheckboxChange = (materialId) => {
        setData((prev) => {
            const updated = [...(prev.cnc_materials || [])];
            console.log("Updated data ::", updated);
            const idx = updated.indexOf(materialId);
            if (idx > -1) updated.splice(idx, 1);
            else updated.push(materialId);
            return { ...prev, cnc_materials: updated };
        });
    };

    const handleMrrInputChange = (materialId, field, value) => {
        setAllMaterials((prev) =>
            prev.map((item) =>
                item._id === materialId
                    ? {
                        ...item,
                        mrr: {
                            ...item.mrr,
                            [field]: value,
                        },
                    }
                    : item
            )
        );
    };

    const handleUpdate = async () => {
        try {
            setLoading(true);

            // 1. Save selected materials to machine
            const machineRes = await upsertMachine(data);
            if (!machineRes?.success) throw new Error();

            // 2. Save MRRs per material
            await Promise.all(
                allMaterials.map((mat) =>
                    upsetMrrValues({
                        machineId: _id,
                        materialId: mat._id,
                        ...mat.mrr,
                    })
                )
            );

            toast.success('Update successful!');
        } catch (err) {
            toast.error('Update failed. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-screen my-2">
            <BackButton additionalClasses="mx-2 my-2" />
            <h1
                onClick={handleUpdate}
                className="float-right px-4 py-2 text-white bg-primaryColor me-10 mt-3 rounded cursor-pointer"
            >
                {'Update'}
            </h1>
            <GenericLoaderComponent
                isLoading={loading}
            />
            <h1 className="text-center text-2xl font-bold my-2 ">
                Select materials for {location.state?.cnc_machine?.toLowerCase()}
            </h1>
            <table className="table-auto w-full border-collapse border border-gray-200 shadow-lg rounded-lg overflow-hidden">
                <thead className="bg-gray-100 text-gray-600 uppercase text-sm font-semibold">
                    <tr>
                        <th className="px-4 py-2 border border-gray-200">Checkbox</th>
                        <th className="px-4 py-2 border-gray-200 flex my-2">
                            <span>Material</span>
                            <span onClick={toggleSortType}>
                                <ArrowUpIcon
                                    additionalClasses={`${sortType === 'asc' ? 'rotate-180' : ''
                                        } h-5 mx-2`}
                                />
                            </span>
                        </th>
                        <th className="px-4 py-2 border border-gray-200">Grade</th>
                        <th className="px-4 py-2 border border-gray-200">Description</th>
                        <th className="px-4 py-2 border border-gray-200">Density (g/cm³)</th>
                        <th className="px-4 py-2 border border-gray-200">Manual Review</th>
                        <th className="px-4 py-2 border border-gray-200">Roughing MRR</th>
                        <th className="px-4 py-2 border border-gray-200">Finishing MRR</th>
                    </tr>
                </thead>
                <tbody>
                    {allMaterials.length ? (
                        allMaterials.map((item, index) => (
                            <tr
                                key={item._id}
                                className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                                    } hover:bg-gray-200 transition duration-200`}
                            >
                                <td className="px-4 py-2 border border-gray-200">
                                    <Form.Check type="checkbox" id={`check-${item._id}`}>
                                        <Form.Check.Input
                                            type="checkbox"
                                            checked={data?.cnc_materials?.includes(item._id)}
                                            onChange={() => handleCheckboxChange(item._id)}
                                            disabled={loading}
                                        />
                                        <Form.Check.Label />
                                    </Form.Check>
                                </td>
                                <td className="px-4 py-2 border border-gray-200">
                                    {item.cnc_material || 'NA'}
                                </td>
                                <td className="px-4 py-2 border border-gray-200">
                                    {item.cnc_grade || 'NA'}
                                </td>
                                <td className="px-4 py-2 border border-gray-200">
                                    {item.cnc_material_desc || 'NA'}
                                </td>
                                <td className="px-4 py-2 border border-gray-200">
                                    {item.cnc_material_density_gms_cm3 || 'NA'}
                                </td>
                                <td className="px-4 py-2 border border-gray-200">
                                    {item.cnc_material_review ? 'true' : 'false'}
                                </td>
                                <td className="px-4 py-2 border border-gray-200">
                                    <input
                                        type="number"
                                        name="cnc_roughing_MRR_mm3_per_min"
                                        value={item.mrr?.cnc_roughing_MRR_mm3_per_min}
                                        onChange={(e) =>
                                            handleMrrInputChange(
                                                item._id,
                                                'cnc_roughing_MRR_mm3_per_min',
                                                e.target.value
                                            )
                                        }
                                        className="w-full px-2 py-1 border border-gray-300 rounded"
                                    />
                                </td>
                                <td className="px-4 py-2 border border-gray-200">
                                    <input
                                        type="number"
                                        name="cnc_finishing_MRR_mm3_per_min"
                                        value={item.mrr?.cnc_finishing_MRR_mm3_per_min}
                                        onChange={(e) =>
                                            handleMrrInputChange(
                                                item._id,
                                                'cnc_finishing_MRR_mm3_per_min',
                                                e.target.value
                                            )
                                        }
                                        className="w-full px-2 py-1 border border-gray-300 rounded"
                                    />
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td
                                colSpan="8"
                                className="px-4 py-2 text-center text-gray-500 border border-gray-200"
                            >
                                No data available
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

        </div >

    );
};

export default AdminDashboardComponents(MachineMaterialSelect);
